import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { setAuthInTransition, clearValidationCache, clearStaleSessionFromStorage } from '../../utils/sessionValidationUtil';
import '../../styles/pages/AuthPages.css';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const successMessage = searchParams.get('message');
  const sessionExpired = searchParams.get('session_expired');
  const returnUrl = searchParams.get('return_url');
  const expiredReason = searchParams.get('reason');

  const { signIn, signInWithProvider } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  // Clear any stale auth transition flags and reset loading state when landing on login page
  useEffect(() => {
    // If we're on the login page, any auth transition should be considered complete/failed
    setAuthInTransition(false);
    clearValidationCache();

    // IMPORTANT: Clear stale sessions that are hours/days old (after overnight sleep)
    // This prevents stuck states where the old expired session blocks new logins
    const clearedStale = clearStaleSessionFromStorage(30); // Clear if expired > 30 minutes ago
    if (clearedStale) {
      console.log('🧹 Cleared stale session on login page load');
    }

    // Reset loading state in case user navigated back from a failed SSO attempt
    setLoading(false);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!email || !password) {
      setError(t('forms.fieldRequired'));
      return;
    }

    try {
      setError(null);
      setLoading(true);

      const { success, error } = await signIn(email, password);

      if (success) {
        // Redirect to return URL if provided, otherwise go to tasks page
        const destination = returnUrl || '/tasks';
        navigate(destination);
      } else {
        setError(error?.message || t('errors.unauthorized'));
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(t('errors.genericError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSsoSignIn = async (provider: 'google' | 'github' | 'linkedin_oidc') => {
    try {
      setError(null);

      // CRITICAL: Set auth transition flag BEFORE initiating SSO redirect
      // This prevents race conditions where validation runs before session is established
      setAuthInTransition(true);
      clearValidationCache();

      // Don't set loading to true - the page will redirect to OAuth provider
      // If we set loading, the spinner shows and never goes away if redirect fails or is slow
      const { success, error } = await signInWithProvider(provider);

      if (!success) {
        // SSO initiation failed, clear the transition flag
        setAuthInTransition(false);
        setError(error?.message || `Failed to sign in with ${provider}.`);
        setLoading(false);
      }

      // If success, Supabase will redirect to OAuth provider
      // The auth_in_transition flag will be cleared in AuthCallback or by expiration
      // Set a safety timeout to clear the flag if redirect doesn't happen
      setTimeout(() => {
        // If we're still on this page after 10 seconds, something went wrong
        if (window.location.pathname === '/login') {
          console.log('⚠️ SSO redirect timeout - clearing auth transition flag');
          setAuthInTransition(false);
        }
      }, 10000);

    } catch (err) {
      console.error(`${provider} SSO error:`, err);
      setAuthInTransition(false);
      setError('An unexpected error occurred during SSO. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-logo">
          <h2>{t('header.brand')}</h2>
        </div>

        <h3 className="auth-title">{t('common.signIn')}</h3>

        {sessionExpired && (
          <div className="auth-error" role="alert" style={{ backgroundColor: '#fff3cd', color: '#856404', borderColor: '#ffeaa7' }}>
            <strong>Session Expired:</strong> Your session has expired. Please sign in again to continue.
            {returnUrl && <div style={{ marginTop: '8px', fontSize: '0.9em' }}>You'll be redirected back to your page after signing in.</div>}
            <div style={{ marginTop: '12px' }}>
              <button
                type="button"
                onClick={() => window.location.reload()}
                style={{
                  padding: '6px 12px',
                  fontSize: '0.85em',
                  backgroundColor: '#856404',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                <i className="bi bi-arrow-clockwise" style={{ marginRight: '6px' }}></i>
                Refresh Page (if stuck)
              </button>
            </div>
          </div>
        )}

        {successMessage && (
          <div className="auth-success">
            {successMessage}
          </div>
        )}

        {error && (
          <div className="auth-error" role="alert">
            {error}
          </div>
        )}

        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="auth-form-group">
            <label htmlFor="email" className="auth-form-label">{t('forms.email')}</label>
            <input
              type="email"
              id="email"
              className="form-control"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              required
            />
          </div>

          <div className="auth-form-group">
            <label htmlFor="password" className="auth-form-label">{t('forms.password')}</label>
            <input
              type="password"
              id="password"
              className="form-control"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              required
            />
            <div className="auth-form-help">
              <Link to="/forgot-password" className="forgot-password-link">
                {t('forms.forgotPassword')}
              </Link>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary auth-submit-btn"
            disabled={loading}
          >
            {loading ? t('forms.signingIn') : t('forms.signInButton')}
          </button>
        </form>

        <div className="sso-divider">
          <span>{t('forms.or')}</span>
        </div>

        <div className="sso-buttons">
          <button type="button" className="btn btn-sso btn-google" onClick={() => handleSsoSignIn('google')} disabled={loading}>
            <img src="/icons/google.svg" alt="Google" /> {t('forms.signInWithGoogle')}
          </button>
          {/* <button type="button" className="btn btn-sso btn-github" onClick={() => handleSsoSignIn('github')} disabled={loading}>
            <img src="/icons/github.svg" alt="GitHub" /> Sign in with GitHub
          </button> */}
          <button type="button" className="btn btn-sso btn-linkedin" onClick={() => handleSsoSignIn('linkedin_oidc')} disabled={loading}>
            <img src="/icons/linkedin.svg" alt="LinkedIn" /> {t('forms.signInWithLinkedIn')}
          </button>
        </div>

        <div className="auth-links">
          {t('forms.dontHaveAccount')} <Link to="/register">{t('common.signUp')}</Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
